import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { onAuthChange, isAuthorizedEditor } from '../services/authService';
import { 
  Interview, 
  Season, 
  PositionType,
  PayType,
  InterviewRound,
  WorkType,
  getInterviews, 
  createInterview, 
  updateInterview, 
  deleteInterview 
} from '../services/interviewService';
import {
  AccessRequest,
  submitAccessRequest,
  getPendingAccessRequests,
  approveAccessRequest,
  denyAccessRequest,
  checkAccessGrant,
  checkUserRequestStatus,
  getAccessGrants,
  revokeAccessGrant,
  manuallyGrantAccess
} from '../services/interviewAccessService';
import MDEditor from '@uiw/react-md-editor';
import { toast } from 'react-toastify';
import '../css/LiquidGlass.css';

const CareerLadder: React.FC = () => {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false); // any signed-in user
  const [canEdit, setCanEdit] = useState(false); // owner-only edits
  const [canViewNotes, setCanViewNotes] = useState(false); // owner-only notes for now
  const [userData, setUserData] = useState<User | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [activeInterview, setActiveInterview] = useState<Interview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'year-desc' | 'year-asc' | 'position'>('year-desc');
  const [filterSeason, setFilterSeason] = useState<Season | ''>('');
  const [filterYear, setFilterYear] = useState<number | ''>('');
  const [searchText, setSearchText] = useState('');
  const [showAccessRequestsModal, setShowAccessRequestsModal] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<AccessRequest[]>([]);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState<'none' | 'pending' | 'approved' | 'denied'>('none');
  const [grants, setGrants] = useState<{ id?: string; email: string; grantedAt?: any; grantedBy?: string }[]>([]);
  const [emailSearch, setEmailSearch] = useState('');
  const [newGrantEmail, setNewGrantEmail] = useState('');
  const [isAddingGrant, setIsAddingGrant] = useState(false);

  // Edit form state
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editCompanyImageUrl, setEditCompanyImageUrl] = useState('');
  const [editPositionName, setEditPositionName] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editWorkType, setEditWorkType] = useState<WorkType | null>(null);
  const [editSeason, setEditSeason] = useState<Season>('Fall');
  const [editYear, setEditYear] = useState<number>(new Date().getFullYear());
  const [editPositionType, setEditPositionType] = useState<PositionType>('New Grad');
  const [editOffer, setEditOffer] = useState(false);
  const [editWithdrew, setEditWithdrew] = useState(false);
  const [editPay, setEditPay] = useState<number | null>(null);
  const [editPayType, setEditPayType] = useState<PayType>('Yearly');
  const [editRound, setEditRound] = useState<InterviewRound | null>(null);
  const [editRating, setEditRating] = useState<number | null>(null);
  const [editInterviewNotes, setEditInterviewNotes] = useState('');

  // Create form state
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyImageUrl, setNewCompanyImageUrl] = useState('');
  const [newPositionName, setNewPositionName] = useState('Software Engineer');
  const [newLocation, setNewLocation] = useState('');
  const [newWorkType, setNewWorkType] = useState<WorkType | null>(null);
  const [newSeason, setNewSeason] = useState<Season>('Summer');
  const [newYear, setNewYear] = useState<number>(new Date().getFullYear() + 1);
  const [newPositionType, setNewPositionType] = useState<PositionType>('New Grad');
  const [newOffer, setNewOffer] = useState(false);
  const [newWithdrew, setNewWithdrew] = useState(false);
  const [newPay, setNewPay] = useState<number | null>(null);
  const [newPayType, setNewPayType] = useState<PayType>('Yearly');
  const [newRound, setNewRound] = useState<InterviewRound | null>(null);
  const [newRating, setNewRating] = useState<number | null>(null);
  const [newInterviewNotes, setNewInterviewNotes] = useState('');

  // Listen to authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      if (user) {
        setIsAuthenticated(true);
        setUserData(user);
        const authorized = isAuthorizedEditor(user);
        setCanEdit(authorized);
        
        // Check if user has access to notes (owner or granted access)
        if (authorized) {
          setCanViewNotes(true);
        } else {
          const hasAccess = await checkAccessGrant(user.email || '');
          setCanViewNotes(hasAccess);
        }
      } else {
        setIsAuthenticated(false);
        setUserData(null);
        setCanEdit(false);
        setCanViewNotes(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch interviews from Firebase
  useEffect(() => {
    const fetchInterviews = async () => {
      try {
        setIsLoading(true);
        const data = await getInterviews();
        setInterviews(data);
      } catch (error) {
        console.error('Error fetching interviews:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInterviews();
  }, []);

  // Fetch pending access requests (owner only)
  useEffect(() => {
    if (canEdit) {
      const fetchRequests = async () => {
        try {
          const requests = await getPendingAccessRequests();
          setPendingRequests(requests);
          const g = await getAccessGrants();
          setGrants(g);
        } catch (error) {
          console.error('Error fetching access requests:', error);
        }
      };
      fetchRequests();
      
      // Refresh when modal opens
      const interval = setInterval(fetchRequests, 5000); // Poll every 5 seconds when owner
      return () => clearInterval(interval);
    }
  }, [canEdit]);

  // Check if user has already requested access
  useEffect(() => {
    if (isAuthenticated && userData?.email && !canEdit) {
      const checkRequest = async () => {
        try {
          const status = await checkUserRequestStatus(userData.email || '');
          setRequestStatus(status);
        } catch (error) {
          console.error('Error checking request status:', error);
        }
      };
      checkRequest();
    }
  }, [isAuthenticated, userData, canEdit]);

  // Mouse tracking for liquid glass effect
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const element = e.currentTarget;
    const rect = element.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    element.style.setProperty('--mouse-x', `${x}%`);
    element.style.setProperty('--mouse-y', `${y}%`);
  };

  // Helper function to normalize pay to yearly equivalent for comparison
  const getYearlyPay = (interview: Interview): number => {
    if (!interview.offer || !interview.pay) return 0;
    // Convert hourly to yearly (assuming 2080 hours/year = 40 hours/week * 52 weeks)
    if (interview.payType === 'Hourly') {
      return interview.pay * 2080;
    }
    return interview.pay;
  };

  // Get unique years for filter
  const uniqueYears = React.useMemo(() => {
    const years = new Set<number>();
    interviews.forEach(interview => {
      years.add(interview.year);
    });
    return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
  }, [interviews]);

  // Filter and sort interviews
  const sortedInterviews = React.useMemo(() => {
    let filtered = [...interviews];

    // Apply text search filter
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      filtered = filtered.filter(interview => {
        const positionMatch = interview.positionName.toLowerCase().includes(searchLower);
        const companyMatch = interview.companyName?.toLowerCase().includes(searchLower);
        const locationMatch = interview.location?.toLowerCase().includes(searchLower);
        return positionMatch || companyMatch || locationMatch;
      });
    }

    // Apply season filter
    if (filterSeason) {
      filtered = filtered.filter(interview => interview.season === filterSeason);
    }

    // Apply year filter
    if (filterYear) {
      filtered = filtered.filter(interview => interview.year === filterYear);
    }

    // Sort the filtered results
    switch (sortBy) {
      case 'year-desc':
        return filtered.sort((a, b) => {
          // First sort by year (descending)
          if (a.year !== b.year) return b.year - a.year;
          // Within same year, sort by season (Fall, Summer, Spring)
          const seasonOrder = { 'Fall': 1, 'Summer': 2, 'Spring': 3 };
          if (seasonOrder[a.season] !== seasonOrder[b.season]) {
            return seasonOrder[a.season] - seasonOrder[b.season];
          }
          // Within same season, sort by pay (highest first) - normalized to yearly
          const payA = getYearlyPay(a);
          const payB = getYearlyPay(b);
          if (payA !== payB) return payB - payA;
          // If same pay or no pay, sort by position name as tiebreaker
          return a.positionName.localeCompare(b.positionName);
        });
      case 'year-asc':
        return filtered.sort((a, b) => {
          // First sort by year (ascending)
          if (a.year !== b.year) return a.year - b.year;
          // Within same year, sort by season (Fall, Summer, Spring)
          const seasonOrder = { 'Fall': 1, 'Summer': 2, 'Spring': 3 };
          if (seasonOrder[a.season] !== seasonOrder[b.season]) {
            return seasonOrder[a.season] - seasonOrder[b.season];
          }
          // Within same season, sort by pay (highest first) - normalized to yearly
          const payA = getYearlyPay(a);
          const payB = getYearlyPay(b);
          if (payA !== payB) return payB - payA;
          // If same pay or no pay, sort by position name as tiebreaker
          return a.positionName.localeCompare(b.positionName);
        });
      case 'position':
        return filtered.sort((a, b) => a.positionName.localeCompare(b.positionName));
        default:
        return filtered;
    }
  }, [interviews, sortBy, filterSeason, filterYear, searchText]);

  const handleEditClick = (interview: Interview) => {
    setActiveInterview(interview);
    setIsEditing(true);
    setEditCompanyName(interview.companyName);
    setEditCompanyImageUrl(interview.companyImageUrl);
    setEditPositionName(interview.positionName);
    setEditLocation(interview.location || '');
    setEditWorkType(interview.workType || null);
    setEditSeason(interview.season);
    setEditYear(interview.year);
    setEditPositionType(interview.positionType);
    setEditOffer(interview.offer || false);
    setEditWithdrew(interview.withdrew || false);
    setEditPay(interview.pay || null);
    setEditPayType(interview.payType || 'Yearly');
    setEditRound(interview.round || null);
    setEditRating(interview.rating || null);
    setEditInterviewNotes(interview.interviewNotes);
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setNewCompanyName('');
    setNewCompanyImageUrl('');
    setNewPositionName('Software Engineer');
    setNewLocation('');
    setNewWorkType(null);
    setNewSeason('Summer');
    setNewYear(new Date().getFullYear() + 1);
    setNewPositionType('New Grad');
    setNewOffer(false);
    setNewWithdrew(false);
    setNewPay(null);
    setNewPayType('Yearly');
    setNewRound(null);
    setNewRating(null);
    setNewInterviewNotes('');
    setActiveInterview(null);
  };

  const handleSaveEdit = async () => {
    if (!activeInterview || !activeInterview.id) {
      alert("Cannot save: interview missing ID");
      return;
    }

    try {
      const updatedInterview: Interview = {
        ...activeInterview,
        companyName: editCompanyName,
        companyImageUrl: editCompanyImageUrl,
        positionName: editPositionName,
        location: editLocation.trim() || null,
        workType: editWorkType,
        season: editSeason,
        year: editYear,
        positionType: editPositionType,
        offer: editOffer,
        withdrew: editWithdrew,
        pay: editOffer && !editWithdrew ? editPay : null,
        payType: editOffer && !editWithdrew ? editPayType : null,
        round: !editOffer && !editWithdrew ? editRound : null,
        rating: editRating,
        interviewNotes: editInterviewNotes
      };

      await updateInterview(updatedInterview);
      setInterviews(interviews.map(i => i.id === activeInterview.id ? updatedInterview : i));
      setIsEditing(false);
      setActiveInterview(null);
    } catch (error) {
      console.error("Error saving interview:", error);
      alert("Failed to save changes: " + (error instanceof Error ? error.message : "Unknown error"));
    }
  };

  const handleSaveNew = async () => {
    try {
      const newInterview: Omit<Interview, 'id' | 'createdAt' | 'updatedAt'> = {
        companyName: newCompanyName,
        companyImageUrl: newCompanyImageUrl,
        positionName: newPositionName,
        location: newLocation.trim() || null,
        workType: newWorkType,
        season: newSeason,
        year: newYear,
        positionType: newPositionType,
        offer: newOffer,
        withdrew: newWithdrew,
        pay: newOffer && !newWithdrew ? newPay : null,
        payType: newOffer && !newWithdrew ? newPayType : null,
        round: !newOffer && !newWithdrew ? newRound : null,
        rating: newRating,
        interviewNotes: newInterviewNotes
      };

      const newId = await createInterview(newInterview);
      const createdInterview: Interview = {
        ...newInterview,
        id: newId
      };

      setInterviews([...interviews, createdInterview]);
      setIsCreating(false);
      
      // Reset form
      setNewCompanyName('');
      setNewCompanyImageUrl('');
      setNewPositionName('Software Engineer');
      setNewLocation('');
      setNewWorkType(null);
      setNewSeason('Fall');
      setNewYear(new Date().getFullYear());
      setNewPositionType('New Grad');
      setNewOffer(false);
      setNewPay(null);
      setNewPayType('Yearly');
      setNewRound(null);
      setNewRating(null);
      setNewInterviewNotes('');
    } catch (error) {
      console.error("Error saving new interview:", error);
      alert("Failed to save new interview: " + (error instanceof Error ? error.message : "Unknown error"));
    }
  };

  const handleDeleteInterview = async (interview: Interview) => {
    const confirmMessage = `Are you sure you want to delete this interview?\n\nPosition: ${interview.positionName}\nCompany: ${interview.companyName || 'N/A'}\nSeason: ${interview.season} ${interview.year}\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteInterview(interview);
      setInterviews(interviews.filter(i => i.id !== interview.id));
      if (activeInterview?.id === interview.id) {
        setActiveInterview(null);
        setIsEditing(false);
      }
      toast.success(`Interview "${interview.positionName}" deleted successfully`);
    } catch (error) {
      console.error("Error deleting interview:", error);
      toast.error("Failed to delete interview");
    }
  };

  const handleRequestAccess = async () => {
    if (!userData?.email) return;
    
    setRequestLoading(true);
    try {
      await submitAccessRequest(userData.email);
      setRequestStatus('pending');
      toast.success('Request submitted! You\'ll be notified when it\'s reviewed.');
    } catch (error: any) {
      if (error.message?.includes("already have")) {
        setRequestStatus('pending');
        toast.info('You already have a pending or approved request.');
      } else {
        toast.error('Failed to submit request: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setRequestLoading(false);
    }
  };

  const handleApproveRequest = async (request: AccessRequest) => {
    if (!userData?.email || !request.id) return;
    
    try {
      await approveAccessRequest(request.id, request.email, userData.email);
      // Refresh requests list
      const requests = await getPendingAccessRequests();
      setPendingRequests(requests);
      toast.success(`Access granted to ${request.email}`);
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error('Failed to approve request');
    }
  };

  const handleDenyRequest = async (request: AccessRequest) => {
    if (!userData?.email || !request.id) return;
    
    try {
      await denyAccessRequest(request.id, request.email, userData.email);
      // Refresh requests list
      const requests = await getPendingAccessRequests();
      setPendingRequests(requests);
      toast.success(`Request denied for ${request.email}`);
    } catch (error) {
      console.error("Error denying request:", error);
      toast.error('Failed to deny request');
    }
  };

  const handleOpenRequestsModal = async () => {
    setEmailSearch(''); // Clear search when opening
    setShowAccessRequestsModal(true);
    if (canEdit) {
      // Refresh requests when opening modal
      try {
        const requests = await getPendingAccessRequests();
        setPendingRequests(requests);
        const g = await getAccessGrants();
        setGrants(g);
      } catch (error) {
        console.error('Error fetching access requests:', error);
      }
    }
  };

  const handleRevokeGrant = async (grantId?: string) => {
    if (!grantId) return;
    try {
      await revokeAccessGrant(grantId);
      toast.success('Access revoked');
      const g = await getAccessGrants();
      setGrants(g);
    } catch (error) {
      console.error('Error revoking grant:', error);
      toast.error('Failed to revoke access');
    }
  };

  const handleAddGrant = async () => {
    if (!newGrantEmail.trim() || !userData?.email) return;
    
    const email = newGrantEmail.trim().toLowerCase();
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check if already has access
    const existingGrant = grants.find(g => g.email.toLowerCase() === email);
    if (existingGrant) {
      toast.info('This email already has access');
      setNewGrantEmail('');
      return;
    }

    setIsAddingGrant(true);
    try {
      await manuallyGrantAccess(email, userData.email);
      toast.success(`Access granted to ${email}`);
      setNewGrantEmail('');
      const g = await getAccessGrants();
      setGrants(g);
    } catch (error: any) {
      console.error('Error adding grant:', error);
      toast.error('Failed to grant access: ' + (error.message || 'Unknown error'));
    } finally {
      setIsAddingGrant(false);
    }
  };

  const seasons: Season[] = ['Fall', 'Spring', 'Summer'];
  const positionTypes: PositionType[] = ['Co-Op', 'Intern', 'New Grad', 'SWE1', 'SWE2', 'Senior SWE'];
  const payTypes: PayType[] = ['Hourly', 'Yearly'];
  const interviewRounds: InterviewRound[] = ['Technical', 'Super Day', 'Hiring Manager', 'Behavioral', 'Phone Screen', 'Team Matching', 'Headcounted'];
  const workTypes: WorkType[] = ['Remote', 'Hybrid', 'In Person'];

  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-white">
        <div className="text-gray-600">Loading interviews...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-white text-gray-900 py-8 sm:py-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">My Interview Experiences</h1>
            <div className="flex items-center gap-3">
              {canEdit && (
                <>
                  <button
                    onClick={handleOpenRequestsModal}
                    onMouseMove={handleMouseMove}
                    className="liquid-glass-button inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.8)', 
                      borderColor: 'rgba(255, 255, 255, 0.6)' 
                    } as React.CSSProperties}
                  >
                    Manage Access or View Requests {pendingRequests.length > 0 && `(${pendingRequests.length})`}
                  </button>
                  <button
                    onClick={handleCreateNew}
                    onMouseMove={handleMouseMove}
                    className="liquid-glass-button liquid-glass-green inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white"
                    style={{ 
                      background: 'rgba(22, 163, 74, 0.8)', 
                      borderColor: 'rgba(22, 163, 74, 0.6)' 
                    } as React.CSSProperties}
                  >
                    + New Interview
                  </button>
                </>
              )}
              {!canEdit && !canViewNotes && (
                <button
                  onClick={isAuthenticated ? handleRequestAccess : undefined}
                  disabled={!isAuthenticated || requestLoading || requestStatus === 'pending'}
                  onMouseMove={isAuthenticated ? handleMouseMove : undefined}
                  className={`liquid-glass-button inline-flex items-center justify-center px-4 py-2 text-sm font-medium ${
                    !isAuthenticated || requestStatus === 'pending' 
                      ? 'opacity-70 cursor-not-allowed text-gray-100' 
                      : 'text-white'
                  }`}
                  style={!isAuthenticated ? {
                    // signed out: greyed
                    background: 'linear-gradient(135deg, rgba(160,160,160,0.7), rgba(120,120,120,0.6))',
                    borderColor: 'rgba(200, 200, 200, 0.5)'
                  } : requestStatus === 'pending' ? {
                    // pending: amber
                    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.9), rgba(245, 158, 11, 0.85))',
                    borderColor: 'rgba(245, 158, 11, 0.6)'
                  } : {
                    // ready to request: vibrant purple/blue
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95), rgba(59, 130, 246, 0.9))',
                    borderColor: 'rgba(99, 102, 241, 0.6)'
                  } as React.CSSProperties}
                >
                  {!isAuthenticated 
                    ? 'Please sign in to request interview notes'
                    : requestStatus === 'pending'
                    ? 'Request Submitted'
                    : 'Request All Interview Notes'
                  }
                </button>
              )}
              {!canEdit && canViewNotes && (
                <button
                  disabled
                  className="liquid-glass-button inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white cursor-default"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.9))',
                    borderColor: 'rgba(16, 185, 129, 0.6)'
                  } as React.CSSProperties}
                >
                  Access Granted
                </button>
              )}
            </div>
          </div>
          
          {/* Filters and Sort */}
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by position, company, or location..."
              className="liquid-glass-card px-4 py-2 text-sm font-medium text-gray-700 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ 
                background: 'rgba(255, 255, 255, 0.8)', 
                borderColor: 'rgba(255, 255, 255, 0.6)',
                minWidth: '200px',
                flex: '1 1 200px'
              } as React.CSSProperties}
            />
            
            <select
              value={filterSeason}
              onChange={(e) => setFilterSeason(e.target.value as Season | '')}
              onMouseMove={handleMouseMove}
              className="liquid-glass-select px-4 py-2 text-sm font-medium text-gray-700 rounded-md"
            >
              <option value="">All Seasons</option>
              {seasons.map(season => (
                <option key={season} value={season}>{season}</option>
              ))}
            </select>
            
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value ? parseInt(e.target.value) : '')}
              onMouseMove={handleMouseMove}
              className="liquid-glass-select px-4 py-2 text-sm font-medium text-gray-700 rounded-md"
            >
              <option value="">All Years</option>
              {uniqueYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              onMouseMove={handleMouseMove}
              className="liquid-glass-select px-4 py-2 text-sm font-medium text-gray-700 rounded-md"
            >
              <option value="year-desc">Sort: Year (Newest)</option>
              <option value="year-asc">Sort: Year (Oldest)</option>
              <option value="position">Sort: Position</option>
            </select>

            {(filterSeason || filterYear || searchText.trim()) && (
              <button
                onClick={() => {
                  setFilterSeason('');
                  setFilterYear('');
                  setSearchText('');
                }}
                onMouseMove={handleMouseMove}
                className="liquid-glass-button px-4 py-2 text-sm font-medium text-gray-700 rounded-md"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.6)', 
                  borderColor: 'rgba(255, 255, 255, 0.4)' 
                } as React.CSSProperties}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
          {sortedInterviews.map((interview) => (
            <div
              key={interview.id || interview.positionName}
              onMouseMove={handleMouseMove}
              className="liquid-glass-card group relative rounded-xl cursor-pointer"
              style={{ 
                background: 'rgba(255, 255, 255, 0.8)', 
                borderColor: 'rgba(255, 255, 255, 0.6)',
                border: '1px solid rgba(255, 255, 255, 0.6)'
              } as React.CSSProperties}
              onClick={() => setActiveInterview(interview)}
            >
              {interview.companyImageUrl && (
                <div className="aspect-video w-full overflow-hidden rounded-t-xl">
                  <img
                    src={interview.companyImageUrl}
                    alt={interview.positionName}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                </div>
              )}
              <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                      {interview.positionName}
                    </h2>
                    {interview.companyName && (
                      <p className="text-sm text-gray-600 mt-1">{interview.companyName}</p>
                    )}
                    {interview.location && (
                      <p className="text-sm text-gray-500 mt-1">📍 {interview.location}</p>
                    )}
                    {interview.workType && (
                      <span className="inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-2.5 py-0.5 text-xs text-purple-700 mt-1">
                        {interview.workType}
                      </span>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600">
                        {interview.season} {interview.year}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600">
                        {interview.positionType}
                      </span>
                      {interview.withdrew ? (
                        <span className="inline-flex items-center rounded-full border border-purple-300 bg-purple-50 px-2.5 py-0.5 text-xs text-purple-700 font-semibold">
                          ↻ Withdrew
                        </span>
                      ) : interview.offer ? (
                        <span className="inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-xs text-green-700 font-semibold">
                          ✓ Offer
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border-2 border-red-300 bg-red-50 px-2.5 py-0.5 text-xs text-red-700 font-bold">
                          ✗ Rejected
                        </span>
                      )}
                      {interview.offer && !interview.withdrew && interview.pay && (
                        <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs text-blue-700 font-semibold">
                          ${interview.pay.toLocaleString()} {interview.payType === 'Hourly' ? '/hr' : '/yr'} USD
                        </span>
                      )}
                      {!interview.offer && !interview.withdrew && interview.round && (
                        <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-0.5 text-xs text-orange-700 font-semibold">
                          Rejected at: {interview.round}
                        </span>
                      )}
                      {interview.rating && (
                        <span className="inline-flex items-center rounded-full border border-yellow-200 bg-yellow-50 px-2.5 py-0.5 text-xs text-yellow-700">
                          {'⭐'.repeat(interview.rating)}{'☆'.repeat(5 - interview.rating)} ({interview.rating}/5)
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {canEdit && (
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(interview);
                      }}
                      onMouseMove={handleMouseMove}
                      className="liquid-glass-button liquid-glass-blue inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-700"
                      style={{ 
                        background: 'rgba(59, 130, 246, 0.15)', 
                        borderColor: 'rgba(59, 130, 246, 0.3)' 
                      } as React.CSSProperties}
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteInterview(interview);
                      }}
                      onMouseMove={handleMouseMove}
                      className="liquid-glass-button liquid-glass-red inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white"
                      style={{ 
                        background: 'rgba(220, 38, 38, 0.8)', 
                        borderColor: 'rgba(220, 38, 38, 0.6)' 
                      } as React.CSSProperties}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

            {sortedInterviews.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No interviews recorded yet.</p>
            {canEdit && (
              <button
                onClick={handleCreateNew}
                onMouseMove={handleMouseMove}
                className="liquid-glass-button liquid-glass-green inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white"
                style={{ 
                  background: 'rgba(22, 163, 74, 0.8)', 
                  borderColor: 'rgba(22, 163, 74, 0.6)' 
                } as React.CSSProperties}
              >
                Add Your First Interview
              </button>
            )}
          </div>
        )}

        {/* Edit Modal */}
        {isEditing && activeInterview && (
          <div 
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6" 
            onClick={() => {
              setIsEditing(false);
              setActiveInterview(null);
            }}
          >
            <div
              className="max-w-6xl w-full max-h-[90vh] rounded-xl border border-gray-200 bg-white text-gray-900 shadow-2xl flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 sm:p-6 pb-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Edit Interview</h2>
              </div>

              <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 sm:py-5">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name *</label>
                    <input 
                      type="text"
                      value={editCompanyName}
                      onChange={(e) => setEditCompanyName(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Company Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Image URL *</label>
                    <input 
                      type="text"
                      value={editCompanyImageUrl}
                      onChange={(e) => setEditCompanyImageUrl(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Position Name *</label>
                    <input 
                      type="text"
                      value={editPositionName}
                      onChange={(e) => setEditPositionName(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Location <span className="text-gray-400">(optional)</span>
                    </label>
                    <input 
                      type="text"
                      value={editLocation}
                      onChange={(e) => setEditLocation(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="City, State"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Work Type <span className="text-gray-400">(optional)</span>
                    </label>
                    <select
                      value={editWorkType || ''}
                      onChange={(e) => setEditWorkType(e.target.value as WorkType | null)}
                      onMouseMove={handleMouseMove}
                      className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select work type...</option>
                      {workTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Season *</label>
                      <select
                        value={editSeason}
                        onChange={(e) => setEditSeason(e.target.value as Season)}
                        onMouseMove={handleMouseMove}
                        className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {seasons.map(season => (
                          <option key={season} value={season}>{season}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Year *</label>
                      <input 
                        type="number"
                        value={editYear}
                        onChange={(e) => setEditYear(parseInt(e.target.value) || new Date().getFullYear())}
                        className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="2000"
                        max="2100"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Position Type *</label>
                    <select
                      value={editPositionType}
                      onChange={(e) => setEditPositionType(e.target.value as PositionType)}
                      onMouseMove={handleMouseMove}
                      className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {positionTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 mb-1.5">
                      <input
                        type="checkbox"
                        checked={editOffer}
                        onChange={(e) => {
                          setEditOffer(e.target.checked);
                          if (e.target.checked) setEditWithdrew(false);
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Received Offer</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 mb-1.5">
                      <input
                        type="checkbox"
                        checked={editWithdrew}
                        onChange={(e) => {
                          setEditWithdrew(e.target.checked);
                          if (e.target.checked) setEditOffer(false);
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Withdrew</span>
                    </label>
                  </div>
                  {editOffer && !editWithdrew && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Pay (USD) *</label>
                        <input 
                          type="number"
                          value={editPay || ''}
                          onChange={(e) => setEditPay(e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Pay Type *</label>
                        <select
                          value={editPayType}
                          onChange={(e) => setEditPayType(e.target.value as PayType)}
                          onMouseMove={handleMouseMove}
                          className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {payTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  {!editOffer && !editWithdrew && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Round Reached *</label>
                      <select
                        value={editRound || ''}
                        onChange={(e) => setEditRound(e.target.value as InterviewRound | null)}
                        onMouseMove={handleMouseMove}
                        className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select round...</option>
                        {interviewRounds.map(round => (
                          <option key={round} value={round}>{round}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Rating (Public) <span className="text-gray-400">(optional)</span>
                    </label>
                    <select
                      value={editRating || ''}
                      onChange={(e) => setEditRating(e.target.value ? parseInt(e.target.value) : null)}
                      onMouseMove={handleMouseMove}
                      className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No rating</option>
                      {[1, 2, 3, 4, 5].map(rating => (
                        <option key={rating} value={rating}>
                          {'⭐'.repeat(rating)}{'☆'.repeat(5 - rating)} ({rating}/5)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Interview Notes (Private) *</label>
                    <MDEditor
                      value={editInterviewNotes}
                      onChange={(val: string | undefined) => setEditInterviewNotes(val || "")}
                      height={400}
                      preview="edit"
                    />
                  </div>
                </div>
              </div>

              <div className="p-5 sm:p-6 pt-4 border-t border-gray-200 flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={handleSaveEdit}
                  onMouseMove={handleMouseMove}
                  className="liquid-glass-button liquid-glass-green inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white"
                  style={{ 
                    background: 'rgba(22, 163, 74, 0.8)', 
                    borderColor: 'rgba(22, 163, 74, 0.6)' 
                  } as React.CSSProperties}
                >
                  Save Changes
                </button>
                {activeInterview.id && (
                  <button
                    onClick={() => handleDeleteInterview(activeInterview)}
                    onMouseMove={handleMouseMove}
                    className="liquid-glass-button liquid-glass-red inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white"
                    style={{ 
                      background: 'rgba(220, 38, 38, 0.8)', 
                      borderColor: 'rgba(220, 38, 38, 0.6)' 
                    } as React.CSSProperties}
                  >
                    Delete
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setActiveInterview(null);
                  }}
                  onMouseMove={handleMouseMove}
                  className="liquid-glass-button inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.8)', 
                    borderColor: 'rgba(255, 255, 255, 0.6)' 
                  } as React.CSSProperties}
                >
                  Cancel
                </button>
            </div>
          </div>
        </div>
        )}

        {/* Create Modal */}
        {isCreating && (
          <div 
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6" 
            onClick={() => setIsCreating(false)}
          >
            <div
              className="max-w-5xl w-full max-h-[90vh] rounded-xl border border-gray-200 bg-white text-gray-900 shadow-2xl flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 sm:p-6 pb-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Create New Interview</h2>
                  <button
                    onClick={() => setIsCreating(false)}
                    onMouseMove={handleMouseMove}
                    className="liquid-glass-button flex-shrink-0 px-3 py-1.5 text-sm font-medium text-gray-700"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.8)', 
                      borderColor: 'rgba(255, 255, 255, 0.6)' 
                    } as React.CSSProperties}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 sm:py-5">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name *</label>
                    <input 
                      type="text"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Company Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Image URL *</label>
                    <input 
                      type="text"
                      value={newCompanyImageUrl}
                      onChange={(e) => setNewCompanyImageUrl(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Position Name *</label>
                    <input 
                      type="text"
                      value={newPositionName}
                      onChange={(e) => setNewPositionName(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Location <span className="text-gray-400">(optional)</span>
                    </label>
                    <input 
                      type="text"
                      value={newLocation}
                      onChange={(e) => setNewLocation(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="City, State"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Work Type <span className="text-gray-400">(optional)</span>
                    </label>
                    <select
                      value={newWorkType || ''}
                      onChange={(e) => setNewWorkType(e.target.value as WorkType | null)}
                      onMouseMove={handleMouseMove}
                      className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select work type...</option>
                      {workTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Season *</label>
                      <select
                        value={newSeason}
                        onChange={(e) => setNewSeason(e.target.value as Season)}
                        onMouseMove={handleMouseMove}
                        className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {seasons.map(season => (
                          <option key={season} value={season}>{season}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Year *</label>
                      <input 
                        type="number"
                        value={newYear}
                        onChange={(e) => setNewYear(parseInt(e.target.value) || new Date().getFullYear())}
                        className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="2000"
                        max="2100"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Position Type *</label>
                    <select
                      value={newPositionType}
                      onChange={(e) => setNewPositionType(e.target.value as PositionType)}
                      onMouseMove={handleMouseMove}
                      className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {positionTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 mb-1.5">
                      <input
                        type="checkbox"
                        checked={newOffer}
                        onChange={(e) => {
                          setNewOffer(e.target.checked);
                          if (e.target.checked) setNewWithdrew(false);
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Received Offer</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 mb-1.5">
                      <input
                        type="checkbox"
                        checked={newWithdrew}
                        onChange={(e) => {
                          setNewWithdrew(e.target.checked);
                          if (e.target.checked) setNewOffer(false);
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Withdrew</span>
                    </label>
                  </div>
                  {newOffer && !newWithdrew && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Pay (USD) *</label>
                        <input 
                          type="number"
                          value={newPay || ''}
                          onChange={(e) => setNewPay(e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Pay Type *</label>
                        <select
                          value={newPayType}
                          onChange={(e) => setNewPayType(e.target.value as PayType)}
                          onMouseMove={handleMouseMove}
                          className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {payTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  {!newOffer && !newWithdrew && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Round Reached *</label>
                      <select
                        value={newRound || ''}
                        onChange={(e) => setNewRound(e.target.value as InterviewRound | null)}
                        onMouseMove={handleMouseMove}
                        className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select round...</option>
                        {interviewRounds.map(round => (
                          <option key={round} value={round}>{round}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Rating (Public) <span className="text-gray-400">(optional)</span>
                    </label>
                    <select
                      value={newRating || ''}
                      onChange={(e) => setNewRating(e.target.value ? parseInt(e.target.value) : null)}
                      onMouseMove={handleMouseMove}
                      className="liquid-glass-select w-full p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No rating</option>
                      {[1, 2, 3, 4, 5].map(rating => (
                        <option key={rating} value={rating}>
                          {'⭐'.repeat(rating)}{'☆'.repeat(5 - rating)} ({rating}/5)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Interview Notes (Private) *</label>
                    <MDEditor
                      value={newInterviewNotes}
                      onChange={(val: string | undefined) => setNewInterviewNotes(val || "")}
                      height={400}
                      preview="edit"
                    />
                  </div>
                </div>
              </div>

              <div className="p-5 sm:p-6 pt-4 border-t border-gray-200 flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={handleSaveNew}
                  onMouseMove={handleMouseMove}
                  className="liquid-glass-button liquid-glass-green inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white"
                  style={{ 
                    background: 'rgba(22, 163, 74, 0.8)', 
                    borderColor: 'rgba(22, 163, 74, 0.6)' 
                  } as React.CSSProperties}
                >
                  Create Interview
                </button>
                <button
                  onClick={() => setIsCreating(false)}
                  onMouseMove={handleMouseMove}
                  className="liquid-glass-button inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.8)', 
                    borderColor: 'rgba(255, 255, 255, 0.6)' 
                  } as React.CSSProperties}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {activeInterview && !isEditing && !isCreating && (
          <div 
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6" 
            onClick={() => setActiveInterview(null)}
          >
            <div
              className="max-w-5xl w-full max-h-[90vh] rounded-xl border border-gray-200 bg-white text-gray-900 shadow-2xl flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Company logo moved into attribute cards */}
              <div className="flex-1 overflow-y-auto p-6 sm:p-8 bg-gray-50">
                {/* Dashboard layout */}
                <div className="grid grid-cols-1 gap-6 min-h-[60vh] lg:flex lg:items-stretch">
                  {/* Left column: attribute cards */}
                  <div className="space-y-6 lg:basis-2/3 lg:flex lg:flex-col">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:row-span-2 flex items-center justify-center">
                        {activeInterview.companyImageUrl ? (
                          <img 
                            src={activeInterview.companyImageUrl} 
                            alt={activeInterview.companyName || activeInterview.positionName}
                            className="max-h-28 sm:max-h-32 object-contain"
                          />
                        ) : (
                          <div className="text-lg font-semibold text-gray-900">—</div>
                        )}
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Title</div>
                        <div className="text-lg font-semibold text-gray-900 break-words">{activeInterview.positionName}</div>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Company</div>
                        <div className="text-lg font-semibold text-gray-900 break-words whitespace-normal leading-relaxed">{activeInterview.companyName || '—'}</div>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Level</div>
                        <div className="text-lg font-semibold text-gray-900">{activeInterview.positionType}</div>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Season</div>
                        <div className="text-lg font-semibold text-gray-900">{activeInterview.season}</div>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Year</div>
                        <div className="text-lg font-semibold text-gray-900">{activeInterview.year}</div>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Location</div>
                        <div className="text-lg font-semibold text-gray-900 break-words whitespace-normal leading-relaxed">
                          {activeInterview.location || '—'}{activeInterview.workType ? ` • ${activeInterview.workType}` : ''}
                        </div>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:col-span-2">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Offer / Result</div>
                        {activeInterview.withdrew ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center rounded-full border-2 border-purple-300 bg-purple-100 px-3 py-1.5 text-sm font-bold text-purple-800">↻ Withdrew</span>
                          </div>
                        ) : activeInterview.offer ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center rounded-full border-2 border-green-300 bg-green-100 px-3 py-1.5 text-sm font-bold text-green-800">✓ Offer</span>
                            {activeInterview.pay && (
                              <span className="inline-flex items-center rounded-full border-2 border-blue-300 bg-blue-100 px-3 py-1.5 text-sm font-bold text-blue-800">
                                💰 ${activeInterview.pay.toLocaleString()} {activeInterview.payType === 'Hourly' ? '/hr' : '/yr'} USD
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center rounded-full border-2 border-red-300 bg-red-100 px-3 py-1.5 text-sm font-bold text-red-800">❌ Rejected</span>
                            {activeInterview.round && (
                              <span className="inline-flex items-center rounded-full border border-orange-300 bg-orange-50 px-3 py-1.5 text-sm font-semibold text-orange-800">Rejected at: {activeInterview.round}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:col-span-2">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Rating</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {activeInterview.rating ? (
                            <span>
                              {'⭐'.repeat(activeInterview.rating)}{'☆'.repeat(5 - activeInterview.rating)} ({activeInterview.rating}/5)
                            </span>
                          ) : '—'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right column: notes */}
                  <div className="lg:basis-1/3 flex flex-col">
                    <div className="bg-white rounded-xl border border-gray-200 p-5 h-full flex flex-col flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">Interview Notes</h3>
                        <span className="text-xs text-gray-400">Private</span>
                      </div>
                      {!canViewNotes && (
                        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800 text-sm">
                          Interview notes are private. You can request view access to the Interview Experiences page to see them.
                        </div>
                      )}
                      {canViewNotes && (
                        activeInterview.interviewNotes ? (
                          <div className="prose prose-sm sm:prose-base max-w-none flex-1 overflow-auto" data-color-mode="light">
                            <MDEditor.Markdown source={activeInterview.interviewNotes} />
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No notes available.</p>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>

                <div className="p-5 sm:p-6 pt-4 border-t border-gray-200 flex items-center gap-3 flex-shrink-0">
                  {canEdit && (
                    <button
                      onClick={() => handleEditClick(activeInterview)}
                      onMouseMove={handleMouseMove}
                      className="liquid-glass-button liquid-glass-blue inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-700"
                      style={{ 
                        background: 'rgba(59, 130, 246, 0.15)', 
                        borderColor: 'rgba(59, 130, 246, 0.3)' 
                      } as React.CSSProperties}
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => setActiveInterview(null)}
                    onMouseMove={handleMouseMove}
                    className="liquid-glass-button inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.8)', 
                      borderColor: 'rgba(255, 255, 255, 0.6)' 
                    } as React.CSSProperties}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* Access Requests Modal */}
        {showAccessRequestsModal && (
          <div 
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6" 
            onClick={() => setShowAccessRequestsModal(false)}
          >
            <div
              className="max-w-5xl w-full max-h-[90vh] rounded-xl border border-gray-200 bg-white text-gray-900 shadow-2xl flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 sm:p-6 pb-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
                    Interview Notes Access Requests
                  </h2>
                  <button
                    onClick={() => setShowAccessRequestsModal(false)}
                    onMouseMove={handleMouseMove}
                    className="liquid-glass-button flex-shrink-0 px-3 py-1.5 text-sm font-medium text-gray-700"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.8)', 
                      borderColor: 'rgba(255, 255, 255, 0.6)' 
                    } as React.CSSProperties}
                  >
                    Close
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={emailSearch}
                    onChange={(e) => setEmailSearch(e.target.value)}
                    placeholder="Search by email..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onMouseMove={handleMouseMove}
                  />
                  {emailSearch && (
                    <button
                      onClick={() => setEmailSearch('')}
                      onMouseMove={handleMouseMove}
                      className="liquid-glass-button px-3 py-2 text-sm font-medium text-gray-700"
                      style={{ 
                        background: 'rgba(255, 255, 255, 0.8)', 
                        borderColor: 'rgba(255, 255, 255, 0.6)' 
                      } as React.CSSProperties}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 sm:py-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-4">
                      Pending Requests
                      {emailSearch && (
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          ({pendingRequests.filter(r => r.email.toLowerCase().includes(emailSearch.toLowerCase())).length} shown)
                        </span>
                      )}
                    </h3>
                    {pendingRequests.length === 0 ? (
                      <div className="text-center py-8 border border-gray-200 rounded-lg bg-white">
                        <p className="text-gray-500">No pending requests.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {pendingRequests.filter(r => !emailSearch || r.email.toLowerCase().includes(emailSearch.toLowerCase())).map((request) => (
                          <div
                            key={request.id}
                            className="bg-white rounded-lg border border-gray-200 p-5"
                          >
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 mb-1 truncate">
                                  {request.email}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Requested: {request.requestedAt?.toDate().toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <button
                                  onClick={() => handleApproveRequest(request)}
                                  onMouseMove={handleMouseMove}
                                  className="liquid-glass-button liquid-glass-green inline-flex items-center justify-center px-3.5 py-2 text-sm font-medium text-white"
                                  style={{ 
                                    background: 'rgba(22, 163, 74, 0.9)', 
                                    borderColor: 'rgba(22, 163, 74, 0.6)' 
                                  } as React.CSSProperties}
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleDenyRequest(request)}
                                  onMouseMove={handleMouseMove}
                                  className="liquid-glass-button liquid-glass-red inline-flex items-center justify-center px-3.5 py-2 text-sm font-medium text-white"
                                  style={{ 
                                    background: 'rgba(220, 38, 38, 0.9)', 
                                    borderColor: 'rgba(220, 38, 38, 0.6)' 
                                  } as React.CSSProperties}
                                >
                                  Deny
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-4">
                      Current Access
                      {emailSearch && (
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          ({grants.filter(g => g.email.toLowerCase().includes(emailSearch.toLowerCase())).length} shown)
                        </span>
                      )}
                    </h3>
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Manually Grant Access
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={newGrantEmail}
                          onChange={(e) => setNewGrantEmail(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddGrant()}
                          placeholder="Enter email address"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={isAddingGrant}
                        />
                        <button
                          onClick={handleAddGrant}
                          disabled={isAddingGrant || !newGrantEmail.trim()}
                          onMouseMove={handleMouseMove}
                          className="liquid-glass-button px-4 py-2 text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ 
                            background: 'rgba(99, 102, 241, 0.9)', 
                            borderColor: 'rgba(99, 102, 241, 0.6)' 
                          } as React.CSSProperties}
                        >
                          {isAddingGrant ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        No email notification will be sent to the recipient
                      </p>
                    </div>
                    {grants.length === 0 ? (
                      <div className="text-center py-8 border border-gray-200 rounded-lg bg-white">
                        <p className="text-gray-500">No active access grants.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {grants.filter(g => !emailSearch || g.email.toLowerCase().includes(emailSearch.toLowerCase())).map((g) => (
                          <div key={g.id} className="bg-white rounded-lg border border-gray-200 p-5">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 mb-1 truncate">{g.email}</p>
                                <p className="text-xs text-gray-500">Granted: {g.grantedAt?.toDate?.().toLocaleDateString?.() || '-'}</p>
                              </div>
                              <button
                                onClick={() => handleRevokeGrant(g.id)}
                                onMouseMove={handleMouseMove}
                                className="liquid-glass-button inline-flex items-center justify-center px-3.5 py-2 text-sm font-medium text-gray-700 flex-shrink-0"
                                style={{ 
                                  background: 'rgba(255, 255, 255, 0.95)', 
                                  borderColor: 'rgba(209, 213, 219, 0.9)' 
                                } as React.CSSProperties}
                              >
                                Revoke
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareerLadder;

